/**
 * Node converters: React Flow to Legacy Format
 * Main orchestrator for converting React Flow node data to legacy HTML format
 */
import { NODE_TYPE_TO_KIND_MAP } from '../../shared/constants/index'
import type { NodeData } from '../../shared/reactFlowTypes'
import { NodeType } from '../../shared/reactFlowTypes'
import { ensureRequiredHtmlFields } from '../htmlFieldHelpers'
import { baseConvertNodeDataToHtml } from './baseConverter'
import {
  convertAutoAttendantNodeData,
  convertCallDistributionNodeData,
  convertChangeCallerIdNodeData,
  convertDayNightNodeData,
  convertExternalTransferNodeData,
  convertInteractiveWebhookNodeData,
  convertSmsNotificationNodeData,
  convertTransferRolloverNodeData,
  convertWebhookNodeData,
} from './nodeSpecificConverters'

const SOUND_CLIP_NODE_TYPES = new Set<NodeType>([
  NodeType.SoundClip,
  NodeType.AutoAttendant,
  NodeType.LeaveVoicemail,
])

function asNonEmptyString(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined
  }

  const normalized = String(value).trim()
  return normalized && normalized !== 'null' && normalized !== 'undefined'
    ? normalized
    : undefined
}

function enforceSoundClipUuidPair(
  html: Record<string, unknown>,
  nodeData: NodeData,
  nodeType: NodeType,
): Record<string, unknown> {
  if (!SOUND_CLIP_NODE_TYPES.has(nodeType)) {
    return html
  }

  const soundClipId = asNonEmptyString(html['sound_clip_id'])
  if (!soundClipId) {
    delete html['sound_clip_uuid']
    return html
  }

  const data = nodeData as Record<string, unknown>
  const hasReactFlowUuid = Object.prototype.hasOwnProperty.call(
    data,
    'soundClipUuid',
  )
  const soundClipUuid = asNonEmptyString(
    hasReactFlowUuid ? data.soundClipUuid : data.sound_clip_uuid,
  )

  if (soundClipUuid) {
    html['sound_clip_uuid'] = soundClipUuid
  } else {
    delete html['sound_clip_uuid']
  }

  return html
}

function enforceScheduleUuidPair(
  html: Record<string, unknown>,
  nodeData: NodeData,
  nodeType: NodeType,
): Record<string, unknown> {
  if (nodeType !== NodeType.Schedule) {
    return html
  }

  const scheduleId = asNonEmptyString(html['schedule_id'])
  if (!scheduleId) {
    delete html['schedule_uuid']
    return html
  }

  const data = nodeData as Record<string, unknown>
  const hasReactFlowUuid = Object.prototype.hasOwnProperty.call(
    data,
    'scheduleUuid',
  )
  const scheduleUuid = asNonEmptyString(
    hasReactFlowUuid ? data.scheduleUuid : data.schedule_uuid,
  )

  if (scheduleUuid) {
    html['schedule_uuid'] = scheduleUuid
  } else {
    delete html['schedule_uuid']
  }

  return html
}

/**
 * Convert any node data to legacy HTML format
 * Routes to node-specific converters when available, otherwise uses base converter
 * @param nodeData - React Flow node data object
 * @param nodeType - React Flow node type
 * @param nodeId - Optional node ID
 * @param messagingGroups - Optional messaging groups for converting SMS sendFrom IDs to phone numbers
 * @returns Legacy HTML format object
 */
export function convertNodeDataToHtml(
  nodeData: NodeData,
  nodeType: NodeType,
  nodeId?: string,
  messagingGroups?: Array<{
    id: number
    phone_numbers?: Array<{
      number?: string
      number_for_display?: string
      is_primary_sms?: boolean
    }>
  }>,
): Record<string, unknown> {
  switch (nodeType) {
    case NodeType.SmsNotification:
      return convertSmsNotificationNodeData(nodeData, nodeId, messagingGroups)
    case NodeType.ExternalTransfer:
      return convertExternalTransferNodeData(nodeData, nodeId)
    case NodeType.AutoAttendant:
      return enforceSoundClipUuidPair(
        convertAutoAttendantNodeData(nodeData, nodeId),
        nodeData,
        nodeType,
      )
    case NodeType.CallDistribution:
      return convertCallDistributionNodeData(nodeData, nodeId)
    case NodeType.InteractiveWebhook:
      return convertInteractiveWebhookNodeData(nodeData, nodeId)
    case NodeType.DayNight:
      return convertDayNightNodeData(nodeData, nodeId)
    case NodeType.TransferAndRollover:
      return convertTransferRolloverNodeData(nodeData, nodeId)
    case NodeType.ChangeCallerId:
      return convertChangeCallerIdNodeData(nodeData, nodeId)
    case NodeType.Webhook:
      return convertWebhookNodeData(nodeData, nodeId)
    default: {
      const baseHtml = baseConvertNodeDataToHtml(
        nodeData as Record<string, unknown>,
        nodeType,
        nodeId,
      )
      const kind = NODE_TYPE_TO_KIND_MAP[nodeType]
      const html = kind ? ensureRequiredHtmlFields(kind, baseHtml) : baseHtml
      return enforceScheduleUuidPair(
        enforceSoundClipUuidPair(html, nodeData, nodeType),
        nodeData,
        nodeType,
      )
    }
  }
}
